/**
 * LAPORAN KASIR HARIAN - FORMAT DETAIL PROFESIONAL
 * Sesuai kaidah akuntansi: PEMASUKAN vs PENGELUARAN
 * Pengeluaran menggunakan pilihan saldo sumber
 */

import React, { useState, useEffect } from 'react';
import {
  Save,
  RefreshCw,
  Plus,
  Trash2,
  X,
  FileText,
  DollarSign,
  CheckCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  Wallet,
  Banknote,
  Building2,
  Car,
  Droplets,
  AlertCircle,
  Info
} from 'lucide-react';

const LaporanKasirDetail = ({ user, api }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [createdLaporan, setCreatedLaporan] = useState(null);

  // State untuk saldo info (current balance dari server)
  const [saldoInfo, setSaldoInfo] = useState({
    saldo_bank: 0,
    saldo_brangkas: 0,
    saldo_kas_kecil: 0,
    saldo_superpel: 0
  });

  // Form data dengan pemisahan PEMASUKAN dan PENGELUARAN
  const [formData, setFormData] = useState({
    // ===== INFORMASI DASAR =====
    report_date: new Date().toISOString().split('T')[0],
    shift_period: 'FULL_DAY',

    // ===== PEMASUKAN =====
    // A. Setoran
    setoran_pagi: ['', '', ''],
    setoran_siang: ['', '', ''],

    // B. Loket Luar (pemasukan dari loket luar)
    deposit_loket_luar: '',
    pelunasan_loket_luar_pagi: '',
    pelunasan_loket_luar_siang: '',

    // C. Transfer Topup (topup = pemasukan dari loket mandiri)
    topup_mandiri: Array(13).fill(''),
    total_topup: '',

    // ===== PENGELUARAN =====
    // D. Pengurangan Kas Kecil (Parkir, Transport) — sumber saldo: Saldo Operasional
    pengurangan_kas_kecil: '',
    saldo_sumber_pengurangan: 'saldo_operasional', // pilihan saldo

    // E. Superpel / Alat Pel (Petty Cash) — sumber saldo: Saldo Operasional
    superpel: '',
    keterangan_superpel: '',
    saldo_sumber_superpel: 'saldo_operasional',

    // F. Pengeluaran Operasional Lain — sumber saldo: Saldo Bank / Brangkas
    pengeluaran_lain: '',
    keterangan_lain: '',
    saldo_sumber_lain: 'saldo_brangkas',
    jenis_pengeluaran_lain: 'BIAYA_OPERASIONAL',

    // ===== RINGKASAN KAS KECIL =====
    penerimaan_kas_kecil: '', // Penerimaan kas kecil (top up saldo kecil)
    total_kas_kecil: '',

    // ===== ADMIN & SALDO AKHIR =====
    penerimaan_admin: '',
    total_admin: '',
    saldo_bank: '',
    saldo_brangkas: '',
  });

  // Format rupiah
  const formatRupiah = (num) => {
    if (!num && num !== 0) return 'Rp 0';
    return 'Rp ' + new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const parseRupiah = (str) => {
    if (!str) return 0;
    const num = str.toString().replace(/[^0-9]/g, '');
    return parseInt(num) || 0;
  };

  // Handle input change dengan auto-format
  const handleInputChange = (field, value, index = null) => {
    if (index !== null) {
      const newArray = [...formData[field]];
      newArray[index] = value;
      setFormData(prev => ({ ...prev, [field]: newArray }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Hitung ringkasan otomatis
  const computeTotals = () => {
    const totalSetoranPagi = formData.setoran_pagi.reduce((sum, v) => sum + parseRupiah(v), 0);
    const totalSetoranSiang = formData.setoran_siang.reduce((sum, v) => sum + parseRupiah(v), 0);
    const totalSetoran = totalSetoranPagi + totalSetoranSiang;
    const totalDepositLuar = parseRupiah(formData.deposit_loket_luar)
      + parseRupiah(formData.pelunasan_loket_luar_pagi)
      + parseRupiah(formData.pelunasan_loket_luar_siang);
    const totalTopupMandiri = formData.topup_mandiri.reduce((sum, v) => sum + parseRupiah(v), 0);
    const totalPemasukan = totalSetoran + totalDepositLuar + totalTopupMandiri;

    const totalPengeluaran = parseRupiah(formData.pengurangan_kas_kecil)
      + parseRupiah(formData.superpel)
      + parseRupiah(formData.pengeluaran_lain);

    return {
      totalSetoranPagi,
      totalSetoranSiang,
      totalSetoran,
      totalDepositLuar,
      totalTopupMandiri,
      totalPemasukan,
      totalPengeluaran,
      saldoBersih: totalPemasukan - totalPengeluaran
    };
  };

  const totals = computeTotals();

  // Fetch saldo info (current balance dari server)
  useEffect(() => {
    const fetchSaldo = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${api}/api/dashboard/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSaldoInfo({
            saldo_bank: data.summary?.saldo_bank || 0,
            saldo_brangkas: data.summary?.saldo_brangkas || 0,
            saldo_kas_kecil: data.summary?.total_kas_kecil || 0,
            saldo_superpel: data.summary?.total_pengurangan_kas_kecil || 0,
          });
        }
      } catch (err) {
        console.error('Gagal ambil saldo:', err);
      }
    };
    fetchSaldo();
  }, [api]);

  // Submit laporan
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.report_date) {
      setError('Mohon isi tanggal laporan');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      // Prepare payload dengan informasi saldo sumber
      const payload = {
        report_date: formData.report_date,
        shift_period: formData.shift_period,
        kasir_id: user?.id,
        kasir_name: user?.username,

        // === PEMASUKAN ===
        setoran_pagi: formData.setoran_pagi.map(parseRupiah),
        setoran_siang: formData.setoran_siang.map(parseRupiah),
        deposit_loket_luar: parseRupiah(formData.deposit_loket_luar),
        pelunasan_loket_luar_pagi: parseRupiah(formData.pelunasan_loket_luar_pagi),
        pelunasan_loket_luar_siang: parseRupiah(formData.pelunasan_loket_luar_siang),
        topup_mandiri: formData.topup_mandiri.map(parseRupiah),
        total_topup: parseRupiah(formData.total_topup),

        // === PENGELUARAN (dengan saldo sumber) ===
        pengurangan_kas_kecil: parseRupiah(formData.pengurangan_kas_kecil),
        saldo_sumber_pengurangan: formData.saldo_sumber_pengurangan,
        superpel: parseRupiah(formData.superpel),
        keterangan_superpel: formData.keterangan_superpel,
        saldo_sumber_superpel: formData.saldo_sumber_superpel,
        pengeluaran_lain: parseRupiah(formData.pengeluaran_lain),
        keterangan_lain: formData.keterangan_lain,
        saldo_sumber_lain: formData.saldo_sumber_lain,
        jenis_pengeluaran_lain: formData.jenis_pengeluaran_lain,

        // === RINGKASAN KAS KECIL ===
        penerimaan_kas_kecil: parseRupiah(formData.penerimaan_kas_kecil),
        total_kas_kecil: parseRupiah(formData.total_kas_kecil),

        // === ADMIN & SALDO AKHIR ===
        penerimaan_admin: parseRupiah(formData.penerimaan_admin),
        total_admin: parseRupiah(formData.total_admin),
        saldo_bank: parseRupiah(formData.saldo_bank),
        saldo_brangkas: parseRupiah(formData.saldo_brangkas),

        // === TOTAL (otomatis) ===
        total_pemasukan: totals.totalPemasukan,
        total_pengeluaran: totals.totalPengeluaran,
        saldo_bersih: totals.saldoBersih,
      };

      const response = await fetch(`${api}/api/laporan-kasir/detail-save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Gagal menyimpan laporan');
      }

      const result = await response.json();
      setCreatedLaporan(result);
      setSuccess(`Laporan berhasil disimpan! ID: ${result.id}`);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      report_date: new Date().toISOString().split('T')[0],
      shift_period: 'FULL_DAY',
      setoran_pagi: ['', '', ''],
      setoran_siang: ['', '', ''],
      deposit_loket_luar: '',
      pelunasan_loket_luar_pagi: '',
      pelunasan_loket_luar_siang: '',
      topup_mandiri: Array(13).fill(''),
      total_topup: '',
      pengurangan_kas_kecil: '',
      saldo_sumber_pengurangan: 'saldo_operasional',
      superpel: '',
      keterangan_superpel: '',
      saldo_sumber_superpel: 'saldo_operasional',
      pengeluaran_lain: '',
      keterangan_lain: '',
      saldo_sumber_lain: 'saldo_brangkas',
      jenis_pengeluaran_lain: 'BIAYA_OPERASIONAL',
      penerimaan_kas_kecil: '',
      total_kas_kecil: '',
      penerimaan_admin: '',
      total_admin: '',
      saldo_bank: '',
      saldo_brangkas: '',
    });
    setCreatedLaporan(null);
    setSuccess('');
  };

  // Komponen pilihan saldo sumber
  const SaldoSumberSelector = ({ fieldName, currentValue }) => (
    <div className="mt-1">
      <label className="text-xs text-gray-500 mb-1 block">Ambil dari saldo:</label>
      <select
        value={currentValue}
        onChange={(e) => handleInputChange(fieldName, e.target.value)}
        className="w-full text-xs px-2 py-1 border border-gray-300 rounded bg-white"
      >
        <option value="saldo_operasional">💰 Saldo Operasional (Kas Kecil)</option>
        <option value="saldo_bank">🏦 Saldo Bank</option>
        <option value="saldo_brangkas">🧰 Saldo Brangkas</option>
      </select>
    </div>
  );

  // Show success state
  if (createdLaporan) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Laporan Kasir Berhasil Dibuat</h1>
          <button
            onClick={resetForm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={18} /> Buat Laporan Baru
          </button>
        </div>

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="text-green-500" size={24} />
            <h3 className="font-semibold text-gray-900">Laporan Tersimpan</h3>
          </div>
          <p className="text-gray-600">ID: {createdLaporan.id}</p>
          <p className="text-gray-600">Tanggal: {createdLaporan.report_date}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Laporan Harian Kasir - Format Detail</h1>
          <p className="text-gray-500">Form pelaporan lengkap dengan pemisahan Pemasukan dan Pengeluaran</p>
        </div>
      </div>

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center justify-between">
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto"><X size={18} /></button>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
          {error}
          <button onClick={() => setError('')} className="ml-auto"><X size={18} /></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ===== INFORMASI DASAR ===== */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="text-blue-600" size={20} />
            <h3 className="font-semibold text-gray-900">Informasi Dasar</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal</label>
              <input
                type="date"
                name="report_date"
                value={formData.report_date}
                onChange={(e) => handleInputChange('report_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
              <select
                name="shift_period"
                value={formData.shift_period}
                onChange={(e) => handleInputChange('shift_period', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="PAGI">Pagi</option>
                <option value="SIANG">Siang</option>
                <option value="FULL_DAY">Full Day</option>
              </select>
            </div>
          </div>
        </div>

        {/* ===== PEMASUKAN ===== */}
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-lg shadow-sm">
          <div className="bg-emerald-600 text-white px-6 py-4 rounded-t-lg flex items-center gap-2">
            <ArrowDownCircle size={22} />
            <h3 className="font-bold text-lg">I. PEMASUKAN</h3>
          </div>

          <div className="p-6 space-y-6">
            {/* A. Setoran */}
            <div className="bg-white rounded-lg p-4 border border-emerald-200">
              <h4 className="font-semibold text-emerald-800 mb-3">A. Setoran</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Setoran Pagi</label>
                  <div className="space-y-2">
                    {formData.setoran_pagi.map((item, idx) => (
                      <input
                        key={`pg-${idx}`}
                        type="text"
                        value={item}
                        onChange={(e) => handleInputChange('setoran_pagi', formatRupiah(parseRupiah(e.target.value)), idx)}
                        placeholder={`Setoran ${idx + 1}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-emerald-700 mt-2 font-medium">Subtotal Pagi: {formatRupiah(totals.totalSetoranPagi)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Setoran Siang</label>
                  <div className="space-y-2">
                    {formData.setoran_siang.map((item, idx) => (
                      <input
                        key={`sg-${idx}`}
                        type="text"
                        value={item}
                        onChange={(e) => handleInputChange('setoran_siang', formatRupiah(parseRupiah(e.target.value)), idx)}
                        placeholder={`Setoran ${idx + 1}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-emerald-700 mt-2 font-medium">Subtotal Siang: {formatRupiah(totals.totalSetoranSiang)}</p>
                </div>
              </div>
              <div className="mt-3 p-3 bg-emerald-100 rounded-lg">
                <p className="text-sm font-bold text-emerald-900">Total Setoran: {formatRupiah(totals.totalSetoran)}</p>
              </div>
            </div>

            {/* B. Loket Luar */}
            <div className="bg-white rounded-lg p-4 border border-emerald-200">
              <h4 className="font-semibold text-emerald-800 mb-3">B. Loket Luar (Pemasukan)</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Setoran Deposit</label>
                  <input type="text" value={formData.deposit_loket_luar}
                    onChange={(e) => handleInputChange('deposit_loket_luar', formatRupiah(parseRupiah(e.target.value)))}
                    placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pelunasan Pagi</label>
                  <input type="text" value={formData.pelunasan_loket_luar_pagi}
                    onChange={(e) => handleInputChange('pelunasan_loket_luar_pagi', formatRupiah(parseRupiah(e.target.value)))}
                    placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pelunasan Siang</label>
                  <input type="text" value={formData.pelunasan_loket_luar_siang}
                    onChange={(e) => handleInputChange('pelunasan_loket_luar_siang', formatRupiah(parseRupiah(e.target.value)))}
                    placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="mt-3 p-3 bg-emerald-100 rounded-lg">
                <p className="text-sm font-bold text-emerald-900">Total Loket Luar: {formatRupiah(totals.totalDepositLuar)}</p>
              </div>
            </div>

            {/* C. Transfer Topup */}
            <div className="bg-white rounded-lg p-4 border border-emerald-200">
              <h4 className="font-semibold text-emerald-800 mb-3">C. Transfer Topup Loket Mandiri</h4>
              <div className="space-y-2">
                {formData.topup_mandiri.map((item, idx) => (
                  <div key={`tp-${idx}`} className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 w-8">{idx + 1}.</span>
                    <input type="text" value={item}
                      onChange={(e) => handleInputChange('topup_mandiri', formatRupiah(parseRupiah(e.target.value)), idx)}
                      placeholder={`Topup ${idx + 1}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                ))}
              </div>
              <div className="mt-3 p-3 bg-emerald-100 rounded-lg">
                <p className="text-sm font-bold text-emerald-900">Total Topup: {formatRupiah(totals.totalTopupMandiri)}</p>
              </div>
            </div>

            {/* Subtotal Pemasukan */}
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-700 text-white p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-bold text-lg">TOTAL PEMASUKAN</span>
                <span className="text-2xl font-bold">{formatRupiah(totals.totalPemasukan)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== PENGELUARAN ===== */}
        <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-lg shadow-sm">
          <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center gap-2">
            <ArrowUpCircle size={22} />
            <h3 className="font-bold text-lg">II. PENGELUARAN</h3>
          </div>

          <div className="p-6 space-y-6">
            {/* Info saldo info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info className="text-blue-600 mt-0.5" size={16} />
                <div className="text-xs text-blue-800">
                  <p className="font-medium mb-1">Saldo Tersedia Saat Ini:</p>
                  <p>Saldo Bank: <span className="font-bold">{formatRupiah(saldoInfo.saldo_bank)}</span> | Saldo Brangkas: <span className="font-bold">{formatRupiah(saldoInfo.saldo_brangkas)}</span> | Kas Kecil: <span className="font-bold">{formatRupiah(saldoInfo.saldo_kas_kecil)}</span></p>
                </div>
              </div>
            </div>

            {/* D. Pengurangan Kas Kecil (Parkir, Transport) */}
            <div className="bg-white rounded-lg p-4 border border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <Car className="text-red-600" size={18} />
                <h4 className="font-semibold text-red-800">D. Pengurangan Kas Kecil (Parkir / Transport)</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nominal</label>
                  <input type="text" value={formData.pengurangan_kas_kecil}
                    onChange={(e) => handleInputChange('pengurangan_kas_kecil', formatRupiah(parseRupiah(e.target.value)))}
                    placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <SaldoSumberSelector fieldName="saldo_sumber_pengurangan" currentValue={formData.saldo_sumber_pengurangan} />
                </div>
              </div>
            </div>

            {/* E. Superpel / Alat Pel */}
            <div className="bg-white rounded-lg p-4 border border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <Droplets className="text-red-600" size={18} />
                <h4 className="font-semibold text-red-800">E. Superpel / Alat Pel (Petty Cash)</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nominal</label>
                  <input type="text" value={formData.superpel}
                    onChange={(e) => handleInputChange('superpel', formatRupiah(parseRupiah(e.target.value)))}
                    placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <SaldoSumberSelector fieldName="saldo_sumber_superpel" currentValue={formData.saldo_sumber_superpel} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Keterangan</label>
                  <input type="text" value={formData.keterangan_superpel}
                    onChange={(e) => handleInputChange('keterangan_superpel', e.target.value)}
                    placeholder="Misal: Superpel, Pembersih, dll" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
            </div>

            {/* F. Pengeluaran Operasional Lain */}
            <div className="bg-white rounded-lg p-4 border border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="text-red-600" size={18} />
                <h4 className="font-semibold text-red-800">F. Pengeluaran Operasional Lain</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nominal</label>
                  <input type="text" value={formData.pengeluaran_lain}
                    onChange={(e) => handleInputChange('pengeluaran_lain', formatRupiah(parseRupiah(e.target.value)))}
                    placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <SaldoSumberSelector fieldName="saldo_sumber_lain" currentValue={formData.saldo_sumber_lain} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Pengeluaran</label>
                  <select value={formData.jenis_pengeluaran_lain}
                    onChange={(e) => handleInputChange('jenis_pengeluaran_lain', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="BIAYA_OPERASIONAL">Biaya Operasional</option>
                    <option value="TRANSFER_BANK">Transfer Bank</option>
                    <option value="DEPOSIT_AGEN">Deposit Agen</option>
                    <option value="PETTY_CASH">Petty Cash</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Keterangan</label>
                  <input type="text" value={formData.keterangan_lain}
                    onChange={(e) => handleInputChange('keterangan_lain', e.target.value)}
                    placeholder="Keterangan..." className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
            </div>

            {/* Subtotal Pengeluaran */}
            <div className="bg-gradient-to-r from-red-500 to-red-700 text-white p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-bold text-lg">TOTAL PENGELUARAN</span>
                <span className="text-2xl font-bold">{formatRupiah(totals.totalPengeluaran)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== RINGKASAN KAS KECIL ===== */}
        <div className="bg-white border border-gray-200 rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Banknote className="text-gray-700" size={20} />
            <h3 className="font-semibold text-gray-900">III. RINGKASAN UANG KAS KECIL</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Penerimaan Kas Kecil (Top Up)</label>
              <input type="text" value={formData.penerimaan_kas_kecil}
                onChange={(e) => handleInputChange('penerimaan_kas_kecil', formatRupiah(parseRupiah(e.target.value)))}
                placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Kas Kecil Saat Ini</label>
              <input type="text" value={formData.total_kas_kecil}
                onChange={(e) => handleInputChange('total_kas_kecil', formatRupiah(parseRupiah(e.target.value)))}
                placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
        </div>

        {/* ===== ADMIN & SALDO AKHIR ===== */}
        <div className="bg-white border border-gray-200 rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="text-gray-700" size={20} />
            <h3 className="font-semibold text-gray-900">IV. ADMIN & SALDO AKHIR</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Penerimaan Admin</label>
              <input type="text" value={formData.penerimaan_admin}
                onChange={(e) => handleInputChange('penerimaan_admin', formatRupiah(parseRupiah(e.target.value)))}
                placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Uang Admin</label>
              <input type="text" value={formData.total_admin}
                onChange={(e) => handleInputChange('total_admin', formatRupiah(parseRupiah(e.target.value)))}
                placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Saldo Bank</label>
              <input type="text" value={formData.saldo_bank}
                onChange={(e) => handleInputChange('saldo_bank', formatRupiah(parseRupiah(e.target.value)))}
                placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Saldo Brangkas</label>
              <input type="text" value={formData.saldo_brangkas}
                onChange={(e) => handleInputChange('saldo_brangkas', formatRupiah(parseRupiah(e.target.value)))}
                placeholder="Rp 0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
        </div>

        {/* ===== SALDO BERSIH (FINAL) ===== */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-200 uppercase tracking-wide">V. SALDO BERSIH</p>
              <p className="text-xs text-blue-200 mt-1">Pemasukan − Pengeluaran</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">{formatRupiah(totals.saldoBersih)}</p>
              <p className="text-xs text-blue-200 mt-1">Tanggal: {formData.report_date}</p>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button type="submit" disabled={submitting}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2">
            {submitting ? <RefreshCw className="animate-spin" size={20} /> : <Save size={20} />}
            {submitting ? 'Menyimpan...' : 'Simpan Laporan'}
          </button>
          <button type="button" onClick={resetForm}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
            Reset
          </button>
        </div>
      </form>
    </div>
  );
};

export default LaporanKasirDetail;
